import React, { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useT, useLanguage } from "@/lib/LanguageContext";
import { relativeTime, errMsg } from "./calendarUtils";
import { RefreshCw, AlertTriangle } from "lucide-react";

// Compact strip (2nd line of GoogleCalendarCard, shown only when connected): relative time
// of the last "busy" check, an amber pill when bookings need a review (drift / an
// unacknowledged Google cancellation), and a "check now" button that runs an immediate
// reconciliation cycle.
// `mismatchCount` is optional: it falls back to `status.mismatch_count` (the aggregate
// returned by GET /tenant/calendar/connection).
//
// BUG-028 — THE PILL IS A BUTTON ONLY WHERE THERE IS SOMEWHERE TO GO. This card is mounted
// on two screens: the calendar, which passes `onShowMismatches` (it can filter its own
// grid), and the availability settings page, which has no grid to filter. There the pill
// rendered as a `<button>` all the same — hover state, pointer cursor, focusable — and
// clicking it did precisely nothing, because the handler was `onShowMismatches &&
// onShowMismatches()`. A control that looks interactive and is not is worse than no
// control: it teaches the practitioner that the mismatch count is not actionable. Absent
// callback ⇒ a plain `<span>`: same information, no promise.
export const SyncStatusStrip = ({ status, mismatchCount, onShowMismatches, onRefresh }) => {
  const t = useT("admin");
  const { lang } = useLanguage();
  const [checking, setChecking] = useState(false);

  const effectiveMismatch = mismatchCount != null ? mismatchCount : (status?.mismatch_count || 0);
  const busyRel = status?.busy_checked_at ? relativeTime(status.busy_checked_at, lang) : null;

  const checkNow = async () => {
    setChecking(true);
    try {
      const { data } = await api.post("/tenant/calendar/reconcile");
      toast.success(t("sync.reconcileToast", { cancelled: data.cancelled, drifted: data.drifted }));
      onRefresh && onRefresh();
    } catch (e) {
      toast.error(errMsg(e, t, "gcal.errorToast"));
    } finally {
      setChecking(false);
    }
  };

  return (
    <div
      data-testid="sync-status-strip"
      className="flex flex-wrap items-center gap-2 mt-2 pt-2 border-t border-eh-border-soft/60 text-xs text-eh-muted"
    >
      <span data-testid="sync-busy-checked">
        {busyRel ? t("sync.busyChecked", { time: busyRel }) : t("sync.busyCheckedNever")}
      </span>

      {effectiveMismatch > 0 && (
        onShowMismatches ? (
          <button
            type="button"
            onClick={onShowMismatches}
            data-testid="sync-mismatch-pill"
            title={t("sync.mismatchHelp")}
            className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full bg-eh-ochre/25 text-eh-ochre-deep hover:bg-eh-ochre/40 transition-colors"
          >
            <AlertTriangle className="w-3 h-3" /> {t("sync.mismatchPill", { n: effectiveMismatch })}
          </button>
        ) : (
          <span
            data-testid="sync-mismatch-pill-static"
            title={t("sync.mismatchHelp")}
            className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full bg-eh-ochre/25 text-eh-ochre-deep"
          >
            <AlertTriangle className="w-3 h-3" /> {t("sync.mismatchPill", { n: effectiveMismatch })}
          </span>
        )
      )}

      <button
        type="button"
        onClick={checkNow}
        disabled={checking}
        data-testid="sync-check-now-btn"
        className="ml-auto inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full border border-eh-border-soft hover:bg-eh-cream text-eh-muted disabled:opacity-50"
      >
        <RefreshCw className={`w-3 h-3 ${checking ? "animate-spin" : ""}`} /> {checking ? t("sync.checking") : t("sync.checkNow")}
      </button>
    </div>
  );
};
